<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[10, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <button type="button" class="profile-trigger" aria-label="Open profile menu">
          <span class="profile-dropdown__anchor min-w-max">
            <slot />
            <VaAvatar :src="admin.avatar" :size="30" color="warning" class="profile-avatar" />
          </span>
        </button>
      </template>
      <VaDropdownContent
        class="profile-dropdown__content md:w-60 px-0 py-3 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <VaList v-for="group in options" :key="group.name">
          <header v-if="group.name" class="profile-dropdown__section">
            {{ t(`user.${group.name}`) }}
          </header>

          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item text-base cursor-pointer"
            v-bind="resolveLinkAttribute(item)"
            @click="onMenuItemClick(item, $event)"
          >
            <VaIcon :name="item.icon" class="menu-item__icon" color="secondary" />
            <span class="menu-item__label">{{ t(`user.${item.name}`) }}</span>
          </VaListItem>
          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { useAdminStore } from '../../../../stores/admin'
import { useImpersonationStore } from '../../../../stores/impersonation'

const admin = useAdminStore()
const impersonationStore = useImpersonationStore()
const router = useRouter()
const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

const { t } = useI18n()

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

withDefaults(
  defineProps<{
    options?: ProfileOptions[]
  }>(),
  {
    options: () => [
      {
        name: 'account',
        separator: true,
        list: [
          {
            name: 'profile',
            to: 'profile',
            icon: 'mso-account_circle',
          },
          {
            name: 'settings',
            to: 'settings',
            icon: 'mso-settings',
          },
        ],
      },
      {
        name: '',
        separator: false,
        list: [
          {
            name: 'logout',
            to: 'logout',
            icon: 'mso-logout',
          },
        ],
      },
    ],
  },
)

const isShown = ref(false)

const resolveLinkAttribute = (item: ProfileListItem) => {
  if (item.name === 'logout' || item.to === 'logout') {
    return {}
  }

  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: '_blank' } : {}
}

const isAgentContext = () => {
  const isImpersonating = !!sessionStorage.getItem('impersonation_session') || !!sessionStorage.getItem('impersonation_token')
  const sessionRole = sessionStorage.getItem('role')
  return isImpersonating || sessionRole === 'agent'
}

const onMenuItemClick = async (item: ProfileListItem, event?: Event) => {
  const isLogoutItem = item.name === 'logout' || item.to === 'logout'
  if (!isLogoutItem) return

  if (event && typeof event.preventDefault === 'function') {
    event.preventDefault()
  }
  if (event && typeof event.stopPropagation === 'function') {
    event.stopPropagation()
  }
  isShown.value = false

  if (isAgentContext()) {
    impersonationStore.endImpersonation()
    return
  }

  await router.push({ name: 'logout' })
}
</script>

<style lang="scss">
.profile-dropdown-wrapper {
  display: inline-flex;
  align-items: center;
}

.profile-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  padding: 0 0.3rem;
  border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--va-background-border) 65%, transparent);
  background: color-mix(in srgb, var(--va-background-element) 58%, transparent);
  box-shadow: inset 0 1px 0 color-mix(in srgb, #fff 7%, transparent);
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.15s ease;

  &:hover {
    background: color-mix(in srgb, var(--va-primary) 12%, var(--va-background-element));
    border-color: color-mix(in srgb, var(--va-primary) 35%, var(--va-background-border));
    box-shadow:
      inset 0 1px 0 color-mix(in srgb, #fff 8%, transparent),
      0 6px 16px color-mix(in srgb, var(--va-primary) 12%, transparent);
  }

  &:active {
    transform: translateY(1px) scale(0.98);
  }
}

.profile-dropdown {
  cursor: pointer;

  &__content {
    border-radius: 14px !important;
    border: 1px solid color-mix(in srgb, var(--va-background-border) 70%, transparent);
    background: color-mix(in srgb, var(--va-background-primary) 96%, transparent) !important;
    backdrop-filter: blur(16px);
    box-shadow:
      0 18px 40px rgba(15, 23, 42, 0.16),
      0 2px 8px rgba(15, 23, 42, 0.06) !important;
    overflow: hidden;

    .menu-item {
      display: flex !important;
      align-items: center;
      justify-content: flex-start;
      gap: 0.55rem;
      min-height: 40px;
      margin: 0 0.65rem;
      padding: 0.45rem 0.85rem !important;
      border-radius: 10px;
      box-sizing: border-box;
      transition: background-color 0.15s ease;
    }

    .menu-item:hover {
      background: var(--hover-color);
    }

    .menu-item__icon {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.25rem;
      margin: 0 !important;
      padding: 0 !important;
    }

    .menu-item__label {
      line-height: 1.2;
    }
  }

  &__section {
    text-transform: uppercase;
    color: var(--va-secondary);
    opacity: 0.78;
    font-weight: 750;
    font-size: 0.68rem;
    letter-spacing: 0.08em;
    padding: 0.2rem 1.15rem 0.45rem;
  }

  &__anchor {
    display: inline-flex;
    align-items: center;
  }
}

.profile-avatar {
  box-shadow:
    0 0 0 2px color-mix(in srgb, var(--va-background-primary) 90%, transparent),
    0 0 0 3px color-mix(in srgb, var(--va-primary) 35%, transparent);
}

@media (max-width: 480px) {
  .profile-trigger {
    width: 34px;
    height: 34px;
    padding: 0;
    border-radius: 11px;
  }
}
</style>
